import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { z, ZodType } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { Loader2 } from "lucide-react";
import { useUserStore } from "@/stores/user";
import { Link, useNavigate } from "react-router-dom";
import { ROUTES_CONFIG } from "@/config/routes.config";
import { toast } from "sonner";
import { TRPCClientError } from "@trpc/react-query";
import { TWhoami } from "@/types/trpc.types";

type LoginForm = {
  email: string;
  password: string;
};

const LoginSchema: ZodType<LoginForm> = z.object({
  email: z.string().email(),
  password: z.string().min(4),
});

const LoginPage = () => {
  const navigate = useNavigate();

  const { trpc } = useTRPC();
  const setUser = useUserStore((s) => s.setUser);

  const { register, formState, getValues } = useForm<LoginForm>({
    resolver: zodResolver(LoginSchema),
  });

  const { mutate, isLoading } = useMutation({
    mutationFn: async (data: LoginForm) => {
      if (LoginSchema.safeParse(data).success === false) {
        throw new Error("Gelieve alle velden correct in te vullen.");
      }
      await trpc.auth.login.mutate({
        email: data.email,
        password: data.password,
      });
      const user = await trpc.auth.whoami.query();
      setUser(user as TWhoami);
      navigate(ROUTES_CONFIG.APP.DASHBOARD.$buildPath({}));
    },
    onError: (err) => {
      if (err instanceof TRPCClientError) {
        toast.error(err.message);
      } else if (err instanceof Error) {
        toast.error(err.message);
      } else {
        toast.error("Er is een onbekende fout opgetreden.");
      }
    },
  });

  const handleSubmit = () => {
    const values = getValues();
    mutate(values);
  };

  return (
    <div className="flex items-center justify-center flex-col h-dvh w-dvw">
      <img
        src="/img/bg/gradient-green-black.png"
        className="absolute top-0 left-0 w-full h-full object-cover -z-20"
        alt=""
      />
      <img
        src="/img/logo/letters-center.svg"
        className="absolute top-4 left-1/2 -translate-x-1/2 h-10 -z-10"
        alt=""
      />

      <div className="max-w-md w-full space-y-8 flex flex-col bg-white/10 p-6 rounded-xl border shadow-sm">
        <div className="space-y-2">
          <h1 className="text-xl font-medium text-center">
            Login in Scoutdeck
          </h1>
          <p className="text-center text-sm">
            Vul jouw emailadres en wachtwoord in om in te loggen
          </p>
        </div>

        <div className="space-y-2">
          <Input
            {...register("email")}
            placeholder="Email"
            shape="pill"
            onKeyDown={(e) => {
              e.key === "Enter" && handleSubmit();
            }}
          />
          <Input
            {...register("password")}
            type="password"
            placeholder="Wachtwoord"
            shape="pill"
            onKeyDown={(e) => {
              e.key === "Enter" && handleSubmit();
            }}
          />
          <div className="flex items-center justify-end">
            <Link
              to={ROUTES_CONFIG.AUTH.FORGOT_PASSWORD.$buildPath({})}
              className="text-xs hover:underline"
            >
              Wachtwoord vergeten?
            </Link>
          </div>
        </div>

        <Button onClick={() => handleSubmit()} variant="bright" shape="pill" disabled={isLoading}>
          {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
          Login
        </Button>
      </div>
    </div>
  );
};

export default LoginPage;
